import {useEffect, useRef, useState} from 'react'
import {isValidPhoneNumber} from 'react-phone-number-input'
import {useBi, useEnvironment, useExperiments, useTranslation} from '@wix/yoshi-flow-editor'

import {getLogger} from '../../../../utils/getLogger'
import {useReservationConfirmationStorage} from '../../storage'
import {getReservationLocationById} from '../../../../utils/reservationLocation'
import {RequestStatus} from '../../../../utils/wrapRequest'
import {PHONE_NUMBER_INCORRECT} from '../../../../services/errors'
import {computeCancellationFeePreview} from '../../../../utils/cancellationFeePreview'
import {getCancellationFeeWarning} from '../../../../utils/cancellationFeeWarning'
import {EXPERIMENTS} from '../../../../utils/constants'
import {parseLocaleFromBM} from '../../../../utils/locale'

export const useHooks = (isOpen: boolean, onClose: () => void) => {
  const {
    reservation,
    reservationLocations,
    orderDetails,
    cancelReservation: cancelReservationApi,
    resetCancelReservationStatus,
    cancelReservationStatus,
    cancelReservationError,
    cancellationFeePreview,
    previewCancellationFee,
    regionalSettings,
  } = useReservationConfirmationStorage()
  const {t} = useTranslation()
  const {isMobile, isPreview} = useEnvironment()
  const {experiments} = useExperiments()
  const isCancellationFeeEnabled = experiments.enabled(EXPERIMENTS.tableReservationsCancellationFee)
  const bi = useBi()
  const logger = getLogger(bi)

  const [phone, setPhone] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [phoneValid, setPhoneValid] = useState(true)
  const modalOpenReported = useRef(false)
  const [biFeePreview, setBiFeePreview] = useState<{
    reservationId: string
    applicable?: boolean
  }>()
  const [isFeePreviewPending, setIsFeePreviewPending] = useState(
    () => isCancellationFeeEnabled && typeof previewCancellationFee === 'function',
  )
  const isCancelReservationLoading = cancelReservationStatus === RequestStatus.LOADING
  const isCancelReservationResolved = cancelReservationStatus === RequestStatus.RESOLVED
  const currentReservationLocation = getReservationLocationById(
    reservation?.details?.reservationLocationId ?? '',
    reservationLocations,
  )

  const phoneIncorrect =
    cancelReservationError?.details?.applicationError?.code === PHONE_NUMBER_INCORRECT

  const cancelReservation = async () => {
    if (isFeePreviewPending) {
      return
    }

    setSubmitted(true)
    const phoneValidForAttempt = isValidPhoneNumber(phone)

    if (reservation) {
      logger.cancelReservationIntent({
        reservation,
        locationId: currentReservationLocation?.location?.id,
        phoneValid: phoneValidForAttempt,
        cancellationFeeApplicable: biFeeApplicable,
        cancellationFeeShown: feeNoticeShown,
      })
    }

    if (!phoneValidForAttempt) {
      setPhoneValid(false)

      return
    }

    if (reservation) {
      const canceledReservation = await cancelReservationApi({
        reservationId: reservation.id ?? '',
        revision: reservation.revision ?? '',
        phone,
      })

      if (canceledReservation?.id) {
        logger.reservationCanceled({
          reservation: canceledReservation,
          locationId: currentReservationLocation?.location?.id,
          isPreview,
          cancellationFeeApplicable: biFeeApplicable,
        })
      }
    }
  }

  const handlePhoneChange = (value: string) => {
    setPhone(value)
    setPhoneValid(isValidPhoneNumber(value))
    resetCancelReservationStatus()
  }

  const phoneError = phoneIncorrect
    ? t(PHONE_ERRORS.notFound)
    : !phoneValid
    ? t(PHONE_ERRORS.invalid)
    : undefined

  const phoneErrorText = submitted ? phoneError : undefined

  const errorMessage = submitted ? cancelReservationError?.message : undefined

  const handleDismiss = (origin: 'no_button' | 'close_button') => {
    logger.cancelReservationDismissed({origin, cancellationFeeShown: feeNoticeShown})
    onClose()
  }

  const feeWarning = getCancellationFeeWarning({
    reservation,
    reservationLocation: currentReservationLocation,
    orderDetails,
    preview: cancellationFeePreview,
    locale: parseLocaleFromBM(regionalSettings),
  })
  const feeApplies = isCancellationFeeEnabled && feeWarning.feeApplies
  const legacyCancellationFeePreview = reservation?.details
    ? computeCancellationFeePreview(
        reservation.cancellationPolicy,
        reservation.details.partySize,
        reservation.details.startDate!,
        currentReservationLocation?.currency,
      )
    : {type: 'NO_POLICY' as const}

  const biFeeApplicable = isCancellationFeeEnabled
    ? biFeePreview?.reservationId === reservation?.id
      ? biFeePreview?.applicable
      : undefined
    : legacyCancellationFeePreview.type === 'FEE_APPLIES'
  const feeNoticeShown =
    feeApplies || (!isFeePreviewPending && legacyCancellationFeePreview.type === 'FEE_APPLIES')
  const needsBiPreview = isCancellationFeeEnabled && typeof previewCancellationFee === 'function'

  useEffect(() => {
    if (!isOpen) {
      modalOpenReported.current = false

      return
    }

    if (
      isFeePreviewPending ||
      modalOpenReported.current ||
      !reservation ||
      (needsBiPreview && biFeePreview?.reservationId !== reservation.id)
    ) {
      return
    }

    logger.cancelReservationModalOpened({
      reservation,
      locationId: currentReservationLocation?.location?.id,
      cancellationFeeShown: feeNoticeShown,
    })
    modalOpenReported.current = true
  }, [
    feeNoticeShown,
    needsBiPreview,
    biFeePreview,
    isFeePreviewPending,
    isOpen,
    currentReservationLocation?.location?.id,
    logger,
    reservation,
  ])

  useEffect(() => {
    setBiFeePreview(undefined)

    if (
      !isCancellationFeeEnabled ||
      !isOpen ||
      !reservation?.id ||
      typeof previewCancellationFee !== 'function'
    ) {
      setIsFeePreviewPending(false)

      return
    }

    let cancelled = false

    setIsFeePreviewPending(true)
    void Promise.resolve(previewCancellationFee(reservation.id))
      .then(
        (preview) => {
          if (!cancelled) {
            setBiFeePreview({reservationId: reservation.id!, applicable: preview?.applies})
          }
        },
        () => {
          if (!cancelled) {
            setBiFeePreview({reservationId: reservation.id!})
          }
        },
      )
      .finally(() => {
        if (!cancelled) {
          setIsFeePreviewPending(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [isCancellationFeeEnabled, isOpen, reservation?.id, previewCancellationFee])

  useEffect(() => {
    if (isCancelReservationResolved) {
      onClose()
    }
  }, [isCancelReservationResolved, onClose])

  useEffect(() => {
    if (!isOpen) {
      setPhone('')
      setSubmitted(false)
      setPhoneValid(true)
      resetCancelReservationStatus()
    }
  }, [isOpen, resetCancelReservationStatus])

  return {
    t,
    isMobile,
    isCancelReservationLoading,
    phone,
    phoneErrorText,
    handlePhoneChange,
    cancelReservation,
    country: currentReservationLocation?.location?.address?.country ?? '',
    errorMessage,
    handleDismiss,
    feeApplies,
    isFeePreviewPending,
    legacyCancellationFeePreview,
    feeWarning,
  }
}

const PHONE_ERRORS = {
  notFound: 'uou-reservations.reservation-confirmation.modal-cancel-phone.error-no-found',
  invalid: 'uou-reservations.reservation-confirmation.modal-cancel-phone.error-invalid',
}
